import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { LogIpSortKey, RequestMissIp } from "shared";
import { createBlock } from "@/api/blocks";
import { ApiException } from "@/api/client";
import { listLogIps } from "@/api/logs";
import { SortHeader, type SortState } from "@/components/SortHeader";
import { UaCell } from "@/components/UaCell";
import { formatDt } from "@/lib/format";

export function IpsPage() {
	const [ips, setIps] = useState<RequestMissIp[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<SortState<LogIpSortKey>>({
		key: "lastSeenAt",
		dir: "desc",
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [blockedIps, setBlockedIps] = useState<Set<string>>(new Set());

	const load = useCallback(
		async (opts?: { append?: boolean; cursor?: string | null }) => {
			setLoading(true);
			setError(null);
			try {
				const res = await listLogIps({
					q: search || undefined,
					cursor: opts?.cursor ?? undefined,
					sort: sort.key,
					order: sort.dir,
				});
				setIps((prev) => (opts?.append ? [...prev, ...res.ips] : res.ips));
				setNextCursor(res.nextCursor);
			} catch (err) {
				setError(err instanceof ApiException ? err.message : "Failed to load");
			} finally {
				setLoading(false);
			}
		},
		[search, sort],
	);

	useEffect(() => {
		void load();
	}, [load]);

	async function blockIp(ip: string) {
		const key = ip.toLowerCase();
		if (blockedIps.has(key)) return;
		if (!confirm(`Block IP "${ip}" globally (all links)?`)) return;
		try {
			await createBlock({ kind: "ip", value: ip });
			setBlockedIps((prev) => new Set(prev).add(key));
		} catch (err) {
			setError(err instanceof ApiException ? err.message : "Block failed");
		}
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">IPs</h1>
				<p className="mt-1 text-xs text-fg-muted">
					Every IP in the miss log, one row per address — how often it hit and how
					many distinct paths it probed.
				</p>
			</div>

			<form
				className="flex flex-wrap gap-2"
				onSubmit={(e) => {
					e.preventDefault();
					setSearch(q.trim());
				}}
			>
				<input
					type="search"
					placeholder="Search IP…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					className="flex-1 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
				/>
				<button
					type="submit"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
				>
					Search
				</button>
			</form>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<div className="overflow-x-auto rounded-xl border border-border">
					<table className="w-full min-w-[900px] text-left text-sm">
					<thead className="border-b border-border bg-surface-raised text-fg-muted">
						<tr>
							<SortHeader
								label="IP"
								sortKey="ip"
								sort={sort}
								onSort={setSort}
								defaultDir="asc"
							/>
							<th className="px-3 py-2 font-medium">Country</th>
							<th
								className="px-3 py-2 font-medium"
								title="User agent of the most recent hit"
							>
								Last UA
							</th>
							<SortHeader
								label="Hits"
								sortKey="hits"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<SortHeader
								label="Paths"
								sortKey="paths"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<SortHeader
								label="Last seen"
								sortKey="lastSeenAt"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<th className="px-3 py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody>
						{ips.map((row) => {
							const label = row.ip || "unknown";
							const blocked = blockedIps.has(row.ip.toLowerCase());
							return (
								<tr
									key={row.ip || "unknown"}
									className="border-b border-border last:border-0"
								>
									<td className="px-3 py-2 font-mono text-xs">{label}</td>
									<td className="px-3 py-2 font-mono text-xs">
										{row.lastCountry ?? "—"}
										{row.lastAsn != null && (
											<span className="text-fg-muted"> · AS{row.lastAsn}</span>
										)}
									</td>
									<UaCell ua={row.lastUserAgent} />
									<td className="px-3 py-2 font-mono text-xs tabular-nums">
										{row.hits}
									</td>
									<td className="px-3 py-2 font-mono text-xs tabular-nums">
										{row.paths}
									</td>
									<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
										{formatDt(row.lastSeenAt)}
									</td>
									<td className="px-3 py-2">
										<div className="flex flex-wrap gap-2 text-xs">
											<Link
												to={`/logs/ip?ip=${encodeURIComponent(row.ip)}`}
												className="text-accent hover:underline"
											>
												Open
											</Link>
											{row.ip && (
												<button
													type="button"
													disabled={blocked}
													className="text-fg-muted hover:text-rose-600 disabled:opacity-40 dark:disabled:text-rose-400/40 disabled:hover:text-fg-muted"
													onClick={() => void blockIp(row.ip)}
												>
													{blocked ? "IP blocked" : "Block IP"}
												</button>
											)}
										</div>
									</td>
								</tr>
							);
						})}
						{!loading && ips.length === 0 && (
							<tr>
								<td colSpan={7} className="px-3 py-8 text-center text-fg-muted">
									No misses logged yet.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{loading && <p className="text-sm text-fg-muted">Loading…</p>}

			{nextCursor && !loading && (
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
					onClick={() => void load({ append: true, cursor: nextCursor })}
				>
					Load more
				</button>
			)}
		</div>
	);
}
